import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Minus, Loader2, LineChart as LineIcon } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid, Legend,
} from "recharts";

const EXAMS = {
  cphq: { name: "CPHQ", stroke: "#a78bfa", badge: "bg-violet-500/15 text-violet-300 border-violet-500/40" },
  cphrm: { name: "CPHRM", stroke: "#22d3ee", badge: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40" },
  chc: { name: "CHC", stroke: "#fbbf24", badge: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  mixed: { name: "Mixed", stroke: "#fb7185", badge: "bg-rose-500/15 text-rose-300 border-rose-500/40" },
};

export function MockTrend() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/certprep/mock/trend`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => setData({ points: [], summary: {}, pass_pct: 70 }));
  }, []);

  // One row per mock; each row carries only its own exam's score so lines don't interpolate across exams
  const rows = useMemo(() => (data?.points || []).map((p, i) => ({
    idx: i + 1,
    label: `#${i + 1} · ${p.date.slice(5)}`,
    [p.exam]: p.score_pct,
  })), [data]);

  if (!data) return <div className="p-6 flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading trend…</div>;

  const activeExams = Object.keys(data.summary);

  return (
    <Card data-testid="mock-trend">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><LineIcon className="w-4 h-4 text-primary" /> Mock score trend</CardTitle>
        <p className="text-xs text-muted-foreground">Dashed line is the {data.pass_pct}% pass mark. Get above it — and stay there.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {rows.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center" data-testid="mock-trend-empty">
            No mocks submitted yet. Your first score plots here.
          </div>
        ) : (
          <div className="h-56 sm:h-64" data-testid="mock-trend-chart">
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 640, height: 224 }}>
              <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: -18 }}>
                <CartesianGrid stroke="#27272a" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: "#a1a1aa", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#3f3f46" }} />
                <YAxis domain={[0, 100]} ticks={[0, 25, 50, 70, 100]} tick={{ fill: "#a1a1aa", fontSize: 11 }} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: 8, fontSize: 12 }}
                  formatter={(v, k) => [`${v}%`, EXAMS[k]?.name || k]}
                />
                <Legend formatter={(k) => EXAMS[k]?.name || k} wrapperStyle={{ fontSize: 12 }} />
                <ReferenceLine y={data.pass_pct} stroke="#34d399" strokeDasharray="6 4" label={{ value: "PASS", fill: "#34d399", fontSize: 10, position: "insideTopRight" }} />
                {activeExams.map(ex => (
                  <Line key={ex} type="monotone" dataKey={ex} stroke={EXAMS[ex].stroke} strokeWidth={2}
                    dot={{ r: 4, strokeWidth: 0, fill: EXAMS[ex].stroke }} activeDot={{ r: 6 }} connectNulls isAnimationActive={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {activeExams.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {activeExams.map(ex => {
              const s = data.summary[ex];
              const Icon = s.delta == null ? Minus : s.delta > 0 ? TrendingUp : s.delta < 0 ? TrendingDown : Minus;
              const tone = s.delta == null || s.delta === 0 ? "text-muted-foreground" : s.delta > 0 ? "text-emerald-300" : "text-rose-300";
              return (
                <div key={ex} className="rounded-lg border border-zinc-800 p-3" data-testid={`mock-trend-summary-${ex}`}>
                  <Badge className={`text-[10px] ${EXAMS[ex].badge}`}>{EXAMS[ex].name}</Badge>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className={`text-2xl font-bold ${s.latest >= data.pass_pct ? "text-emerald-300" : ""}`}>{s.latest}%</span>
                    <span className={`text-xs flex items-center gap-0.5 ${tone}`}>
                      <Icon className="w-3.5 h-3.5" />{s.delta == null ? "first" : `${s.delta > 0 ? "+" : ""}${s.delta}`}
                    </span>
                  </div>
                  <div className="text-[11px] text-muted-foreground">best {s.best}% · {s.passes}/{s.count} passed</div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
